import { Button, Divider, Group } from '@melio/penny';
import { useMelioIntl } from '@melio/platform-i18n';
import type { Plan } from '@melio/subscriptions-api';
import { useSubscription } from '@melio/subscriptions-sdk';
import { useMemo } from 'react';

import { useSubscriptionPlanTier } from '../../../utils';

type PlanCardButtonProps = {
  isCurrent: boolean;
  isSaving?: boolean;
  isDisabled?: boolean;
  isFreePlan?: boolean;
  isSwitchingBillingCycle?: boolean;
  planName: Plan['title'];
  planId: string;
  onClick: () => void;
};

export const PlanCardButton = ({
  isCurrent,
  isSaving,
  isDisabled,
  isFreePlan,
  isSwitchingBillingCycle,
  planName,
  planId,
  onClick,
}: PlanCardButtonProps) => {
  const { formatMessage } = useMelioIntl();
  const { isDowngradedPlan } = useSubscriptionPlanTier();
  const { subscription } = useSubscription();
  const variant = isFreePlan || (isCurrent && !isSwitchingBillingCycle) ? 'tertiary' : 'primary';

  const label = useMemo(() => {
    if (!subscription.planId && isFreePlan) {
      return formatMessage('activities.subscription.plans.card.startForFree');
    }

    if (isDowngradedPlan(planId)) {
      return formatMessage('activities.subscription.plans.card.downgrade');
    }

    if (isCurrent) {
      if (isFreePlan || !isSwitchingBillingCycle) {
        return formatMessage('activities.subscription.plans.card.current');
      }
      return formatMessage('activities.subscription.plans.card.switchToYearly');
    }

    return formatMessage('activities.subscription.plans.card.choosePlan', { planName });
  }, [
    isCurrent,
    isFreePlan,
    planName,
    isSwitchingBillingCycle,
    formatMessage,
    isDowngradedPlan,
    planId,
    subscription.planId,
  ]);

  return (
    <Group variant="vertical" spacing="s">
      <Button
        data-testid={`subscription-plan-card-button-${planId}`}
        label={label}
        isDisabled={isDisabled}
        isLoading={isSaving}
        variant={variant}
        onClick={onClick}
      />
      <Divider />
    </Group>
  );
};
